import { Bar, Line, Pie } from "react-chartjs-2";
import { CHART_TYPES } from "../constants/cardRegistry";
import {
  getBaseBarChartOptions,
  getBaseBarDataset,
  getBaseLineChartOptions,
  getBaseLineDataset,
  getBasePieChartOptions,
} from "../config/chartConfig";

/**
 * Renders a chart based on the selected chartType.
 *
 * Props:
 * - chartType: one of CHART_TYPES values
 * - labels: array of label strings
 * - dataValues: array of numeric values (single dataset)
 * - datasets: array of {label, data, color} objects (multi-dataset, overrides dataValues)
 * - color: hex color string (single dataset)
 * - chartTitle: label for the dataset
 * - chartOptions: custom options to merge (e.g. CHART_CONFIGS.currency)
 */
const ChartRenderer = ({
  chartType,
  labels,
  dataValues,
  datasets,
  color,
  chartTitle = "",
  chartOptions = {},
}) => {
  if (!chartType || chartType === CHART_TYPES.BOLD_NUMBER) {
    return null;
  }

  const isMulti = datasets && datasets.length > 0;

  if (
    chartType === CHART_TYPES.LINE ||
    chartType === CHART_TYPES.STRAIGHT_LINE ||
    chartType === CHART_TYPES.DOTTED_LINE
  ) {
    const lineConfig =
      chartType === CHART_TYPES.DOTTED_LINE
        ? { borderDash: [5, 5], tension: 0, fill: false }
        : chartType === CHART_TYPES.STRAIGHT_LINE
          ? { tension: 0 }
          : {};

    const chartDatasets = isMulti
      ? datasets.map((ds) =>
          getBaseLineDataset(ds.label, ds.data, ds.color, lineConfig),
        )
      : [getBaseLineDataset(chartTitle, dataValues, color, lineConfig)];

    const data = { labels, datasets: chartDatasets };
    const options = getBaseLineChartOptions({
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        legend: isMulti
          ? { display: true, position: "top" }
          : { display: false },
      },
    });
    return <Line data={data} options={options} />;
  }

  if (chartType === CHART_TYPES.BAR) {
    const chartDatasets = isMulti
      ? datasets.map((ds) => getBaseBarDataset(ds.label, ds.data, ds.color))
      : [getBaseBarDataset(chartTitle, dataValues, color)];

    const data = { labels, datasets: chartDatasets };
    const options = getBaseBarChartOptions({
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        legend: isMulti
          ? { display: true, position: "top" }
          : { display: false },
      },
    });
    return <Bar data={data} options={options} />;
  }

  if (chartType === CHART_TYPES.PIE) {
    const bgColors = labels.map(
      (_, i) => color + ((40 + i * 30) % 256).toString(16).padStart(2, "0"),
    );
    const data = {
      labels,
      datasets: [
        {
          data: dataValues,
          backgroundColor: bgColors,
          borderColor: color,
          borderWidth: 1,
        },
      ],
    };
    return <Pie data={data} options={getBasePieChartOptions(chartOptions)} />;
  }

  // Fallback: line chart
  const data = {
    labels,
    datasets: [getBaseLineDataset(chartTitle, dataValues, color)],
  };
  return <Line data={data} options={getBaseLineChartOptions(chartOptions)} />;
};

export default ChartRenderer;
